/****************************************************
 * Module Name:  departmentMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { Component } from "react";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { Avatar, Box, Button, Chip, Tooltip } from "@mui/material";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import Constants from "../../utility/constants";
import CommonSnackbar from "../common/commonSnackBar";
import AddDepartment from "./addDepartment";
import DepartmentProxy from "../../utility/proxy/manager/manufacturingProxy/departmentProxy";
import AddDepartmentGroup from "./addDepartmentGroup";
import DepartmentGroupProxy from "../../utility/proxy/manager/manufacturingProxy/departmentGroupProxy";
import NoPermission from "../common/noPermission";
import PageTitle from "../common/pageTitle";

export class DepartmentMaster extends Component {
  constructor(props) {
    super(props);

    this.state = {
      department: [],

      deptOptions: [],
      descriptionOptions: [],

      showAddDepartmentModal: false,
      departmentGroupId: "",
      editOperation: false,
      snackbarVariant: "",
      snackbarMessage: "",
      showSnackbar: "",
      sourceData: {},
      columnNames: [],
      loadingButton: false,
      showDepartmentLoadSpinner: true,
      showAddDepartmentGroupModal: false,
      local_Permission: Constants.USER_PERMISSION.filter(
        (iPermission) =>
          iPermission.permissionid ===
          Constants.PERMISSIONS.MANUFACTURING_MASTER_DEPARTMENT
      ),
      accesDenied: false
    };

    this.checkPermission()
  }

  componentDidMount() {
    PageTitle("MANUFACTURING_MASTER_DEPARTMENT");
  }

  checkPermission = () => {
    let allowed = Utility.isReadAllowed(this.state.local_Permission)
    if (!allowed) {
      this.setState({
        accesDenied: true
      })
    } else {
      this.fetchDepartmentData(false);
    }
  }

  setSnackbarState = (show, message, variant) => {
    this.setState({
      showSnackbar: show,
      snackbarMessage: message,
      snackbarVariant: variant,
    })
  }

  fetchDepartmentData = (bAnimateSpinner) => {
    bAnimateSpinner && this.setState({ showDepartmentLoadSpinner: true });
    DepartmentProxy.GetAllDepartmentDetail((responseData) => {
      if (responseData) {
        const uniqueFilterValues = {
          deptOptions: Utility.getDistinctOptions(responseData, 'departmentName'),
          descriptionOptions: Utility.getDistinctOptions(responseData, 'departmentDescription'),
        }
        this.setState({
          department: responseData,
          showDepartmentLoadSpinner: false,
        })
        const filterMap = this.generateFilterMap(Array.isArray(responseData) ? responseData : [responseData]);
        this.prepareColumns(filterMap, uniqueFilterValues);
      } else {
        this.setState({
          showDepartmentLoadSpinner: false,
        })
        this.setSnackbarState(true, Constants.APiFailErrorMessage, "error");
      }
    }
    );
  };

  generateFilterMap = (responseData) => {
    const filterMap = {};

    // Process department Heads
    const departmentHeads = responseData.flatMap((dept) =>
      dept.heads.map((head) => ({
        id: head.userid,
        username: head.username,
      }))
    );
    filterMap.heads = [
      ...new Set(
        departmentHeads.map((dg) => `${dg.id} | ${dg.username}`)
      ),
    ];

    // Process department users
    const departmentUsers = responseData.flatMap((user) =>
      user.users.map((u) => ({
        id: u.userid,
        username: u.username,
      }))
    );

    filterMap.users = [
      ...new Set(
        departmentUsers.map(
          (user) => `${user.id} | ${user.username}`
        )
      ),
    ];

    // Process department Group
    const departmentGroups = responseData.map((dept) => ({
      id: dept.departmentGroupId,
      username: dept.departmentGroupName,
    }));

    filterMap.departmentGroupName = [
      ...new Set(
        departmentGroups.map((group) => `${group.id} | ${group.username}`)
      ),
    ];

    return filterMap;
  };

  prepareColumns = (filterMap, uniqueFilterValues) => {
    let columnNames = [];
    const columnData = this.initialColumns(uniqueFilterValues);

    columnData.forEach(iCol => {
      if (iCol.field === "heads") {
        iCol.type = "singleSelect";
        iCol.valueOptions = filterMap ? filterMap.departmentheads : [];
        iCol.filterOperators = Utility.customFilters(filterMap, iCol.field, 'userid', 'username');
      }
      if (iCol.field === "users") {
        iCol.type = "singleSelect";
        iCol.valueOptions = filterMap ? filterMap.departmentusers : [];
        iCol.filterOperators = Utility.customFilters(filterMap, iCol.field, 'userid', 'username');
      }
      if (iCol.field === "departmentGroupName") {
        iCol.type = "singleSelect";
        iCol.valueOptions = filterMap ? filterMap.departmentgroups : [];
        iCol.filterOperators = Utility.customFilters(filterMap, iCol.field, 'departmentGroupId', 'departmentGroupName');
      }
      columnNames.push(iCol);
    });

    this.setState({
      columnNames: columnNames
    })
  }

  initialColumns = (uniqueFilterValues) => [
    {
      field: "departmentId",
      headerName: "ID",
      headerAlign: "center",
      type: 'number',
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
      align: "center",
    },
    {
      field: "departmentName",
      headerName: "DEPARTMENT",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: uniqueFilterValues ? uniqueFilterValues.departmentName : [],
      filterOperators: Utility.combinedFilters(),
      flex: 1,
    },
    {
      field: "departmentDescription",
      headerName: "DESCRIP",
      headerAlign: "center",
      align: "center",
      flex: 1,
      type: "singleSelect",
      valueOptions: uniqueFilterValues ? uniqueFilterValues.departmentDescription : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "maxdayspacketallowed",
      headerName: "MAX DAYS PACKET ALLOWED",
      headerAlign: "center",
      align: "center",
      width: 210,
      type: "number",
      renderCell: renderCellExpand,
    },
    {
      field: "departmentGroupName",
      headerName: "DEPARTMENT GROUP",
      headerAlign: "center",
      align: "center",
      flex: 3,
      renderCell: (params) => (
        <Box sx={{ minHeight: "auto", mt: 1 }}>
          <Chip
            avatar={<Avatar>{params.row.departmentGroupId}</Avatar>}
            label={params.row.departmentGroupName}
            onClick={() => {
              this.getSingleDepartmentGroup(params.row.departmentGroupId);
            }}
            sx={{ margin: "0px 10px 10px 0px" }}
          />
        </Box>
      ),
      sortComparator: (v1, v2) => Utility.sortOperatorFn(v1, v2, "departmentGroupName")
    },
    {
      field: "heads",
      headerName: "DEPARTMENT HEADS",
      headerAlign: "center",
      align: "center",
      flex: 3,
      renderCell: (params) => (
        <Box sx={{ minHeight: "auto", mt: 1 }}>
          {params.value.map((head, index) => (
            <Chip
              key={index}
              avatar={<Avatar>{head.userid}</Avatar>}
              label={head.username}
              // onClick={() => {
              //   this.setState({
              //     showAddDepartmentGroupModal: true,
              //     editOperation: false,
              //     sourceData: undefined,
              //     departmentGroupId: params.row.departmentGroupId,
              //   });
              // }}
              sx={{ margin: "0px 10px 10px 0px" }}
            />
          ))}
        </Box>
      ),
      sortComparator: (v1, v2) => Utility.sortOperatorFn(v1, v2, "username")
    },
    {
      field: "users",
      headerName: "DEPARTMENT USERS",
      headerAlign: "center",
      align: "center",
      flex: 3,
      renderCell: (params) => (
        <Box sx={{ minHeight: "auto", mt: 1 }}>
          {params.value.map((user, index) => (
            <Chip
              key={index}
              avatar={<Avatar>{user.userid}</Avatar>}
              label={user.username}
              // onClick={() => {
              //   this.setState({
              //     showAddDepartmentGroupModal: true,
              //     editOperation: false,
              //     sourceData: undefined,
              //     departmentGroupId: params.row.departmentGroupId,
              //   });
              // }}
              sx={{ margin: "0px 10px 10px 0px" }}
            />
          ))}
        </Box>
      ),
      sortComparator: (v1, v2) => Utility.sortOperatorFn(v1, v2, "username")
    },
    {
      field: "edit",
      headerName: "EDIT",
      headerAlign: "center",
      align: "center",
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Box
            style={{ textAlign: "center", cursor: "pointer" }}
            onClick={() => {
              this.setState({
                showAddDepartmentModal: true,
                editOperation: true,
                sourceData: params.row,
              });
            }}
          >
            {" "}
            <BorderColorIcon color="primary" />
          </Box>
        );
      },
    },
  ];

  getSingleDepartmentGroup = (departmentGroupId) => {
    DepartmentGroupProxy.GetSingleDepartmentGroupDetail(
      departmentGroupId,
      (response) => {
        if (response) {
          this.setState({
            sourceData: response,
            showAddDepartmentGroupModal: true,
            editOperation: true,
          });
        } else {
          this.setState({
            showSnackbar: true,
            showDepartmentGroupLoadSpinner: false,
          });
        }
      }
    );
  };

  hideAddDepartmentModal = () => {
    this.setState({
      showAddDepartmentModal: false,
      showAddDepartmentGroupModal: false,
    });
  };

  handleValidation = (departmentData) => {
    //TODO do validation if needed here
    return true;
  };

  addDepartment = (departmentData) => {
    this.setState({ loadingButton: true });
    if (this.state.editOperation) {
      DepartmentProxy.UpdateDepartment(departmentData, (response) => {
        if (response) {
          this.fetchDepartmentData(true);
          this.hideAddDepartmentModal();
          this.setState({ loadingButton: false });
          this.setSnackbarState(true, "Department updated successfully", "success");
        } else {
          this.setSnackbarState(true, Constants.APiFailErrorMessage, "error");
          this.setState({ loadingButton: false });
        }
      });
    } else {
      DepartmentProxy.AddDepartment(departmentData, (response) => {
        if (response) {
          this.fetchDepartmentData(true);
          this.hideAddDepartmentModal();
          this.setState({ loadingButton: false });
          this.setSnackbarState(true, "Department created successfully", "success");
        } else {
          this.setSnackbarState(true, Constants.APiFailErrorMessage, "error");
          this.setState({ loadingButton: false });
        }
      });
    }
  };

  addDepartmentGroup = (departmentGroupData) => {
    this.setState({ loadingButton: true });
    DepartmentGroupProxy.UpdateDepartmentGroup(
      departmentGroupData,
      (response) => {
        if (response) {
          this.fetchDepartmentData(true);
          this.setState({ loadingButton: false, showAddDepartmentGroupModal: false });
          this.setSnackbarState(true, "Department Group updated successfully", "success");
        } else {
          this.setSnackbarState(true, Constants.APiFailErrorMessage, "error");
          this.setState({ loadingButton: false });
        }
      }
    );
  };

  render() {
    let allowed = Utility.isReadAllowed(this.state.local_Permission)
    if (!allowed) {
      return <NoPermission />
    }
    const rows = this.state.department;
    const columns = Utility.isUpdateAllowed(this.state.local_Permission)
      ? this.state.columnNames
      : this.state.columnNames.filter((col) => col.field !== "edit");
    return (
      <Box>
        <CommonSnackbar
          show={this.state.showSnackbar}
          message={this.state.snackbarMessage}
          variant={this.state.snackbarVariant}
          autoHide={Constants.SnackBarCloseTimer}
          handleClose={() => this.setState({ showSnackbar: false })}
          title={this.state.snackbarVariant}
        />
        <br />
        {this.state.showAddDepartmentModal ? (
          <AddDepartment
            Show={true}
            handleClose={this.hideAddDepartmentModal}
            handleSave={this.addDepartment}
            handleValidation={this.handleValidation}
            editOperation={this.state.editOperation}
            sourceData={this.state.sourceData}
            loading={this.state.loadingButton}
          />
        ) : (
          <Box />
        )}

        {this.state.showAddDepartmentGroupModal ? (
          <AddDepartmentGroup
            Show={true}
            handleClose={this.hideAddDepartmentModal}
            handleSave={this.addDepartmentGroup}
            handleValidation={this.handleValidation}
            editOperation={this.state.editOperation}
            sourceData={this.state.sourceData}
            loading={this.state.loadingButton}
            departmentGroupId={this.state.departmentGroupId}
          />
        ) : (
          <Box />
        )}
        <Box px={1}>
          <Box
            sx={{
              backgroundColor: '#fff',
              color: '#000',
              width: "100%",
              position: "relative",
              "& .MuiTablePagination-selectLabel": {
                marginTop: "15px",
              },
              "& .MuiTablePagination-displayedRows": {
                marginTop: "15px",
              },
              "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                fontWeight: Constants.defaultFontWeight,
              },
              "& .css-cc8tf1": {
                fontWeight: Constants.defaultFontWeight,
              },
              height: "83vh",
            }}
          >
            <DataGridPro
              disableRowSelectionOnClick={true}
              getRowHeight={() => "auto"}
              sx={{
                '& .MuiDataGrid-columnHeader': {
                  color: '#fff',
                  backgroundColor: 'grey',
                },
                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                  py: "4px",
                },
                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                  py: "12px",
                },
                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                  py: "18px",
                },
                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
              }}
              density="compact"
              loading={this.state.showDepartmentLoadSpinner}
              slots={{ toolbar: GridToolbar }}
              rows={rows}
              columns={columns}
              getRowId={(row) => row.departmentId}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    // Hide columns status and traderName, the other columns will remain visible
                    departmentId: false,
                  },
                },
                pagination: {
                  paginationModel: Constants.pageSize,
                },
              }}
              pageSizeOptions={Constants.pageSizeOptions}
              pagination
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
            <Box style={{ position: "absolute", top: 5, right: "270px" }}>
              {this.state.showDepartmentLoadSpinner ? (
                <CircularProgress size="1rem" />
              ) : (
                <Tooltip title="Reload List">
                  <RefreshOutlinedIcon
                    color="primary"
                    fontSize="medium"
                    onClick={() => this.fetchDepartmentData(true)}
                    sx={{ cursor: "pointer" }}
                  />
                </Tooltip>
              )}

              {Utility.isAddAllowed(this.state.local_Permission) ? (
                <Tooltip title="Add Department">
                  <Button
                    color="info"
                    variant="outlined"
                    size="small"
                    sx={{ ml: "5px" }}
                    onClick={() =>
                      this.setState({
                        showAddDepartmentModal: true,
                        editOperation: false,
                        sourceData: {},
                      })
                    }
                    startIcon={<AddBoxOutlinedIcon size="large" />}
                  >
                    Department
                  </Button>
                </Tooltip>
              ) : null}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
}

export default DepartmentMaster;
